import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  X,
  Save,
  RefreshCw,
  Users,
  TrendingUp,
  Eye,
  Edit2,
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  accepting: {
    color: 'emerald',
    bgColor: 'bg-emerald-500/20',
    borderColor: 'border-emerald-500/50',
    textColor: 'text-emerald-400',
    label: 'Accepting Providers',
    icon: CheckCircle
  },
  near_capacity: {
    color: 'amber',
    bgColor: 'bg-amber-500/20',
    borderColor: 'border-amber-500/50',
    textColor: 'text-amber-400',
    label: 'Near Capacity',
    icon: AlertCircle
  },
  launching_soon: {
    color: 'slate',
    bgColor: 'bg-slate-500/20',
    borderColor: 'border-slate-500/50',
    textColor: 'text-slate-400',
    label: 'Launching Soon',
    icon: Clock
  },
  unavailable: {
    color: 'red',
    bgColor: 'bg-red-500/20',
    borderColor: 'border-red-500/50',
    textColor: 'text-red-400',
    label: 'Unavailable',
    icon: X
  }
};

const US_STATES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming'
};

// State Row Component
const StateRow = ({ stateCode, stateName, stateData, onEdit }) => {
  const config = STATUS_CONFIG[stateData?.status] || STATUS_CONFIG.unavailable;
  const IconComponent = config.icon;
  const slotsUsed = (stateData?.totalSlots || 20) - (stateData?.slotsRemaining || 0);
  const percentage = ((slotsUsed / (stateData?.totalSlots || 20)) * 100).toFixed(0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl ${config.bgColor} border ${config.borderColor} hover:scale-[1.01] transition-transform`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* State Code Badge */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${config.bgColor} ${config.textColor}`}>
            {stateCode}
          </div>
          
          {/* State Info */}
          <div>
            <h4 className="text-white font-semibold">{stateName}</h4>
            <div className="flex items-center gap-2 mt-1">
              <IconComponent className={`w-4 h-4 ${config.textColor}`} />
              <span className={`text-sm ${config.textColor}`}>{config.label}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          {/* Slots Progress */}
          <div className="text-right">
            <p className="text-white text-sm font-medium">
              {slotsUsed} / {stateData?.totalSlots || 20} filled
            </p>
            <div className="w-32 h-2 bg-slate-700 rounded-full mt-1 overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all ${
                  percentage > 80 ? 'bg-red-500' : percentage > 50 ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>

          {/* Edit Button */}
          <button
            onClick={() => onEdit(stateCode, stateData)}
            className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 text-white transition-colors"
            data-testid={`edit-state-${stateCode}`}
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Edit Modal Component
const EditStateModal = ({ stateCode, stateData, onClose, onSave }) => {
  const [status, setStatus] = useState(stateData?.status || 'unavailable');
  const [slotsRemaining, setSlotsRemaining] = useState(stateData?.slotsRemaining || 20);
  const [totalSlots, setTotalSlots] = useState(stateData?.totalSlots || 20);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/insurance/states`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stateCode,
          status,
          slotsRemaining: parseInt(slotsRemaining),
          totalSlots: parseInt(totalSlots)
        })
      });

      if (response.ok) {
        toast.success(`${US_STATES[stateCode]} updated successfully`);
        onSave();
        onClose();
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update state status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        className="bg-slate-900 rounded-2xl p-6 w-full max-w-md border border-slate-700"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{US_STATES[stateCode]}</h3>
              <p className="text-slate-400 text-sm">{stateCode}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Status Selector */}
          <div>
            <label className="block text-slate-400 text-sm mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                const IconComponent = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setStatus(key)}
                    className={`p-3 rounded-xl border transition-all flex items-center gap-2 ${
                      status === key 
                        ? `${config.bgColor} ${config.borderColor} ${config.textColor}` 
                        : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid={`status-${key}`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span className="text-sm">{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slots Configuration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-400 text-sm mb-2">Slots Remaining</label>
              <input
                type="number"
                value={slotsRemaining}
                onChange={e => setSlotsRemaining(e.target.value)}
                min={0}
                max={totalSlots}
                className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-cyan-500"
                data-testid="slots-remaining-input"
              />
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-2">Total Slots</label>
              <input
                type="number"
                value={totalSlots}
                onChange={e => setTotalSlots(e.target.value)}
                min={1}
                className="w-full px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-white focus:outline-none focus:border-cyan-500"
                data-testid="total-slots-input"
              />
            </div>
          </div>

          {/* Preview */}
          <div className={`p-4 rounded-xl ${STATUS_CONFIG[status].bgColor} border ${STATUS_CONFIG[status].borderColor}`}>
            <p className="text-slate-400 text-xs mb-2">Preview</p>
            <div className="flex items-center justify-between">
              <span className={`font-medium ${STATUS_CONFIG[status].textColor}`}>
                {STATUS_CONFIG[status].label}
              </span>
              <span className="text-white">
                {totalSlots - slotsRemaining} / {totalSlots} filled
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 rounded-xl bg-cyan-500 text-slate-900 font-bold hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="save-state-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Applications List Component
const ApplicationsList = ({ applications, onRefresh }) => {
  const [expandedApp, setExpandedApp] = useState(null);

  const updateStatus = async (appId, newStatus) => {
    try {
      const response = await fetch(`${API}/api/insurance/applications/${appId}?status=${newStatus}`, {
        method: 'PATCH'
      });
      if (response.ok) {
        toast.success('Application status updated');
        onRefresh();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <div className="space-y-3">
      {applications.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No applications yet</p>
        </div>
      ) : (
        applications.map(app => (
          <motion.div
            key={app.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-4 rounded-xl bg-slate-800/50 border border-slate-700"
          >
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setExpandedApp(expandedApp === app.id ? null : app.id)}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h4 className="text-white font-medium">{app.companyName}</h4>
                  <p className="text-slate-400 text-sm">{app.firstName} {app.lastName} • {app.state}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  app.status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                  app.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                  app.status === 'reviewing' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-slate-500/20 text-slate-400'
                }`}>
                  {app.status || 'pending'}
                </span>
                {expandedApp === app.id ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </div>

            <AnimatePresence>
              {expandedApp === app.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="pt-4 mt-4 border-t border-slate-700 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-slate-500 text-xs">Email</p>
                      <p className="text-white">{app.email}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Phone</p>
                      <p className="text-white">{app.phone}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Fleet Size</p>
                      <p className="text-white">{app.fleetSize}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">City</p>
                      <p className="text-white">{app.city}</p>
                    </div>
                    {app.currentInsurer && (
                      <div>
                        <p className="text-slate-500 text-xs">Current Insurer</p>
                        <p className="text-white">{app.currentInsurer}</p>
                      </div>
                    )}
                    {app.currentPremium && (
                      <div>
                        <p className="text-slate-500 text-xs">Current Premium</p>
                        <p className="text-white">{app.currentPremium}</p>
                      </div>
                    )}
                    <div className="col-span-2">
                      <p className="text-slate-500 text-xs">Submitted</p>
                      <p className="text-white">{new Date(app.submittedAt || app.createdAt).toLocaleString()}</p>
                    </div>
                    
                    {/* Status Actions */}
                    <div className="col-span-2 flex gap-2 pt-2">
                      <button
                        onClick={() => updateStatus(app.id, 'reviewing')}
                        className="px-3 py-2 rounded-lg bg-amber-500/20 text-amber-400 text-sm hover:bg-amber-500/30 transition-colors"
                      >
                        Mark Reviewing
                      </button>
                      <button
                        onClick={() => updateStatus(app.id, 'approved')}
                        className="px-3 py-2 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm hover:bg-emerald-500/30 transition-colors"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => updateStatus(app.id, 'rejected')}
                        className="px-3 py-2 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors"
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))
      )}
    </div>
  );
};

// Main Component
export const InsuranceStateManager = () => {
  const [states, setStates] = useState({});
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingState, setEditingState] = useState(null);
  const [activeTab, setActiveTab] = useState('states'); // states, applications
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statesRes, appsRes] = await Promise.all([
        fetch(`${API}/api/insurance/states`),
        fetch(`${API}/api/insurance/applications`)
      ]);

      if (statesRes.ok) {
        const statesData = await statesRes.json();
        setStates(statesData.states || {});
      }

      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.applications || []);
      }
    } catch (error) {
      console.error('Error fetching insurance data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate stats
  const stats = {
    accepting: Object.values(states).filter(s => s.status === 'accepting').length,
    nearCapacity: Object.values(states).filter(s => s.status === 'near_capacity').length,
    launchingSoon: Object.values(states).filter(s => s.status === 'launching_soon').length,
    totalApplications: applications.length,
    pendingApplications: applications.filter(a => a.status === 'pending').length
  };

  // Filter states by status
  const filteredStates = Object.entries(states).filter(([code, data]) => {
    if (statusFilter === 'all') return true;
    return data.status === statusFilter;
  });

  return (
    <div className="space-y-6" data-testid="insurance-state-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Insurance State Management</h2>
          <p className="text-slate-400 mt-1">Manage founding provider availability by state</p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 rounded-xl bg-slate-800 text-white hover:bg-slate-700 transition-colors flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
          <p className="text-emerald-400 text-sm">Accepting</p>
          <p className="text-2xl font-bold text-white">{stats.accepting}</p>
        </div>
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-amber-400 text-sm">Near Capacity</p>
          <p className="text-2xl font-bold text-white">{stats.nearCapacity}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-500/10 border border-slate-500/30">
          <p className="text-slate-400 text-sm">Launching Soon</p>
          <p className="text-2xl font-bold text-white">{stats.launchingSoon}</p>
        </div>
        <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
          <p className="text-cyan-400 text-sm">Total Applications</p>
          <p className="text-2xl font-bold text-white">{stats.totalApplications}</p>
        </div>
        <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
          <p className="text-purple-400 text-sm">Pending Review</p>
          <p className="text-2xl font-bold text-white">{stats.pendingApplications}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-4">
        <button
          onClick={() => setActiveTab('states')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
            activeTab === 'states' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <MapPin className="w-4 h-4" />
          State Management
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
            activeTab === 'applications' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4" />
          Applications
          {stats.pendingApplications > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs">
              {stats.pendingApplications}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'states' && (
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex gap-2">
            {['all', 'accepting', 'near_capacity', 'launching_soon', 'unavailable'].map(filter => (
              <button
                key={filter}
                onClick={() => setStatusFilter(filter)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  statusFilter === filter 
                    ? 'bg-cyan-500/20 text-cyan-400' 
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {filter === 'all' ? 'All States' : STATUS_CONFIG[filter]?.label || filter}
              </button>
            ))}
          </div>

          {/* States Grid */}
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
              <p className="text-slate-400 mt-4">Loading states...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {filteredStates.map(([code, data]) => (
                <StateRow
                  key={code}
                  stateCode={code}
                  stateName={US_STATES[code]}
                  stateData={data}
                  onEdit={(code, data) => setEditingState({ code, data })}
                />
              ))}
            </div>
          )}

          {/* Add New State Button */}
          <button
            onClick={() => setEditingState({ code: '', data: null })}
            className="w-full p-4 rounded-xl border-2 border-dashed border-slate-700 text-slate-400 hover:border-cyan-500/50 hover:text-cyan-400 transition-colors flex items-center justify-center gap-2"
          >
            <MapPin className="w-5 h-5" />
            Add State
          </button>
        </div>
      )}

      {activeTab === 'applications' && (
        <ApplicationsList applications={applications} onRefresh={fetchData} />
      )}

      {/* Edit Modal */}
      <AnimatePresence>
        {editingState && (
          <EditStateModal
            stateCode={editingState.code}
            stateData={editingState.data}
            onClose={() => setEditingState(null)}
            onSave={fetchData}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default InsuranceStateManager;
